import { supabase } from '../lib/supabase';
import {
  SubmitApplicationRequest,
  SubmitApplicationResponse,
  CancelApplicationRequest,
  CancelApplicationResponse,
  GetMyApplicationsResponse,
} from '../types';

/**
 * 수강 신청 제출
 */
export const submitApplication = async (
  request: SubmitApplicationRequest
): Promise<SubmitApplicationResponse> => {
  try {
    console.log('Submit application request:', request);

    // Get auth token
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    if (!token) {
      return {
        success: false,
        error: {
          code: 'UNAUTHORIZED',
          message: '로그인이 필요합니다.',
        },
      };
    }

    // Use fetch directly to get full error response
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!;
    const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY!;

    const response = await fetch(`${supabaseUrl}/functions/v1/submit-application`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify(request),
    });

    const responseData = await response.json();
    console.log('Submit application response:', { status: response.status, data: responseData });

    return responseData;
  } catch (error: any) {
    console.error('Submit application error:', error);
    return {
      success: false,
      error: {
        code: 'UNEXPECTED_ERROR',
        message: '예상치 못한 오류가 발생했습니다.',
        details: error.message,
      },
    };
  }
};

/**
 * 수강 신청 취소
 */
export const cancelApplication = async (
  request: CancelApplicationRequest
): Promise<CancelApplicationResponse> => {
  try {
    console.log('=== cancelApplication 요청 ===');
    console.log('request:', request);

    // Get auth token
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    if (!token) {
      return {
        success: false,
        error: {
          code: 'UNAUTHORIZED',
          message: '로그인이 필요합니다.',
        },
      };
    }

    // Use fetch directly to get full error response
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!;
    const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY!;

    const response = await fetch(`${supabaseUrl}/functions/v1/cancel-application`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify(request),
    });

    console.log('=== cancelApplication 응답 ===');
    console.log('status:', response.status);
    console.log('statusText:', response.statusText);

    const responseData = await response.json();
    console.log('responseData:', JSON.stringify(responseData, null, 2));

    return responseData;
  } catch (error: any) {
    console.error('Cancel application error:', error);
    return {
      success: false,
      error: {
        code: 'UNEXPECTED_ERROR',
        message: '예상치 못한 오류가 발생했습니다.',
        details: error.message,
      },
    };
  }
};

/**
 * 내 신청 내역 조회
 */
export const getMyApplications = async (
  statusFilter?: 'registered' | 'waiting' | 'cancelled' | 'all'
): Promise<GetMyApplicationsResponse> => {
  try {
    console.log('Get my applications - statusFilter:', statusFilter);

    // Get auth token
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    if (!token) {
      return {
        success: false,
        error: {
          code: 'UNAUTHORIZED',
          message: '로그인이 필요합니다.',
        },
      };
    }

    // Use fetch directly
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!;
    const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY!;

    const url = statusFilter && statusFilter !== 'all'
      ? `${supabaseUrl}/functions/v1/get-my-applications?status=${statusFilter}`
      : `${supabaseUrl}/functions/v1/get-my-applications`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'apikey': supabaseAnonKey,
      },
    });

    const responseData = await response.json();
    console.log('Get my applications response:', { status: response.status, data: responseData });

    return responseData;
  } catch (error: any) {
    console.error('Get applications error:', error);
    return {
      success: false,
      error: {
        code: 'UNEXPECTED_ERROR',
        message: '예상치 못한 오류가 발생했습니다.',
        details: error.message,
      },
    };
  }
};

/**
 * 결제 처리 (PG 콜백 결과 전달)
 * Phase 3: 결제 페이지에서 PG SDK 완료 후 호출
 */
export interface ProcessPaymentRequest {
  application_id: string;    // 신청 ID (필수)
  imp_uid: string;           // PG사 거래 고유번호
  merchant_uid: string;      // application_number
  imp_success: boolean;
  paid_amount: number;
  payment_method: string;    // 결제 수단 (card, bank, cash, free)
  mock_mode?: boolean;       // Mock 모드 플래그 (true: PG 검증 스킵)
  error_msg?: string;
}

export interface ProcessPaymentResponse {
  success: boolean;
  message?: string;
  data?: {
    application_id: string;
    merchant_uid: string;
    pg_transaction_id: string;
    amount: number;
  };
  error?: {
    code: string;
    message: string;
    details?: string;
  };
}

export const processPayment = async (
  request: ProcessPaymentRequest
): Promise<ProcessPaymentResponse> => {
  try {
    console.log('Process payment request:', request);

    // Get auth token
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    if (!token) {
      return {
        success: false,
        error: {
          code: 'UNAUTHORIZED',
          message: '로그인이 필요합니다.',
        },
      };
    }

    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!;
    const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY!;

    const response = await fetch(`${supabaseUrl}/functions/v1/process-payment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify(request),
    });

    const responseData = await response.json();
    console.log('Process payment response:', { status: response.status, data: responseData });

    return responseData;
  } catch (error: any) {
    console.error('Process payment error:', error);
    return {
      success: false,
      error: {
        code: 'UNEXPECTED_ERROR',
        message: '결제 처리 중 오류가 발생했습니다.',
        details: error.message,
      },
    };
  }
};

// =====================================================
// 자녀 관련 함수 제거 - 항상 본인 신청 구조로 변경
// Child, CreateChildRequest, UpdateChildRequest, getMyChildren,
// addChild, updateChild, deleteChild 함수 삭제됨
// =====================================================
